import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  Home,
  Calendar,
  Users,
  Package,
  ShoppingCart,
  Mail,
  FileCheck,
  FileSearch,
  Globe,
  Settings,
  ChevronDown,
  PanelLeftClose,
  PanelLeftOpen,
  Scale,
  CheckCircle2,
  Trophy,
  Layers,
  BookmarkCheck,
  MessageSquareText,
  Bot,
  Archive,
  Gavel,
} from 'lucide-react';
import { useTheme } from '@/providers/ThemeProvider';
import { hasMenuPermission, MenuPermissionKey } from '@/config/menuPermissions';

type IconCmp = React.ComponentType<{ size?: number; className?: string }>;

interface NavLeaf {
  to: string;
  label: string;
  icon: IconCmp;
  permissionKey: MenuPermissionKey;
}
interface NavAccordion {
  label: string;
  icon: IconCmp;
  children: { to: string; label: string; permissionKey: MenuPermissionKey }[];
}
interface NavGroup {
  group: string;
  items: (NavLeaf | NavAccordion)[];
}

const NAV: NavGroup[] = [
  {
    group: 'Principal',
    items: [
      { to: '/', label: 'Visão geral', icon: Home, permissionKey: 'DASHBOARD' },
      { to: '/agenda', label: 'Agenda', icon: Calendar, permissionKey: 'AGENDA' },
    ],
  },
  {
    group: 'Licitações',
    items: [
      { to: '/consults', label: 'Busca de oportunidade', icon: FileSearch, permissionKey: 'OPPORTUNITIES' },
      { to: '/tender-calendar', label: 'Calendário de oportunidades', icon: Calendar, permissionKey: 'OPPORTUNITIES' },
      { to: '/analyst', label: 'Analista IA', icon: Bot, permissionKey: 'ANALYST' },
      { to: '/favorites', label: 'Licitações em Participação', icon: BookmarkCheck, permissionKey: 'FAVORITES' },
      { to: '/favorites-archived', label: 'Participações arquivadas', icon: Archive, permissionKey: 'FAVORITES_ARCHIVED' },
      {
        label: 'Em andamento',
        icon: CheckCircle2,
        children: [
          { to: '/ongoing', label: 'Processos', permissionKey: 'ONGOING' },
          { to: '/participation', label: 'Participação', permissionKey: 'PARTICIPATION' },
          { to: '/chat-monitoring', label: 'Monitor de licitação', permissionKey: 'CHAT_MONITORING' },
        ],
      },
      { to: '/ganhas',      label: 'Homologadas',      icon: Trophy, permissionKey: 'HOMOLOGATED' },
      { to: '/resultados',  label: 'Resultados',       icon: Layers, permissionKey: 'RESULTS' },
      {
        label: 'Concorrentes',
        icon: Users,
        children: [
          { to: '/competitors',             label: 'Concorrentes', permissionKey: 'COMPETITORS' },
          { to: '/concorrentes-resultados', label: 'Resultados', permissionKey: 'COMPETITOR_RESULTS' },
        ],
      },
    ],
  },
  {
    group: 'Operação',
    items: [
      { to: '/orders',         label: 'Empenhos & O.S.',     icon: ShoppingCart, permissionKey: 'ORDERS' },
      { to: '/contracts',      label: 'Contratos',           icon: Scale, permissionKey: 'CONTRACTS' },
      { to: '/products',       label: 'Produtos & serviços', icon: Package, permissionKey: 'PRODUCTS' },
      { to: '/communications', label: 'Ofícios & prazos',    icon: Mail, permissionKey: 'COMMUNICATIONS' },
    ],
  },
  {
    group: 'Documentação',
    items: [
      { to: '/atestados', label: 'Atestados técnicos', icon: FileCheck, permissionKey: 'CERTIFICATES' },
      { to: '/documents', label: 'Documentos e certidões', icon: BookmarkCheck, permissionKey: 'DOCUMENTS' },
      { to: '/portals',   label: 'Portais',            icon: Globe, permissionKey: 'PORTALS' },
    ],
  },
  {
    group: 'Jurídico',
    items: [
      { to: '/legal', label: 'Gestão jurídica', icon: Gavel, permissionKey: 'LEGAL' },
    ],
  },
  {
    group: 'Gestão',
    items: [
      { to: '/settings', label: 'Configurações', icon: Settings, permissionKey: 'SETTINGS' },
    ],
  },
];

function isAccordion(i: NavLeaf | NavAccordion): i is NavAccordion {
  return 'children' in i;
}

interface SidebarProps {
  collapsed: boolean;
  setCollapsed: (v: boolean) => void;
  mobileOpen: boolean;
  setMobileOpen: (v: boolean) => void;
  user: { name?: string; role?: string; menuKeys?: string[] } | null;
}

export function Sidebar({
  collapsed,
  setCollapsed,
  mobileOpen,
  setMobileOpen,
  user,
}: SidebarProps) {
  const { effective } = useTheme();
  const width = collapsed ? 'lg:w-16' : 'lg:w-56';
  const visibleNavigation = NAV.map((group) => ({
    ...group,
    items: group.items.flatMap((item) => {
      if (isAccordion(item)) {
        const children = item.children.filter((child) => hasMenuPermission(user, child.permissionKey));
        return children.length ? [{ ...item, children }] : [];
      }
      return hasMenuPermission(user, item.permissionKey) ? [item] : [];
    }),
  })).filter((group) => group.items.length > 0);

  const closeMobile = () => setMobileOpen(false);

  return (
    <>
      {/* Overlay mobile */}
      {mobileOpen && (
        <button
          type="button"
          aria-label="Fechar menu"
          className="fixed inset-0 bg-black/50 z-30 lg:hidden"
          onClick={closeMobile}
        />
      )}

      <aside
        className={[
          'fixed lg:relative h-screen z-40 flex flex-col overflow-hidden',
          'bg-surface dark:bg-surface-dark',
          'border-r border-line dark:border-line-dark',
          'transition-[width,transform] duration-200 ease-out',
          'w-56',
          mobileOpen ? 'translate-x-0' : '-translate-x-full',
          'lg:translate-x-0',
          width,
        ].join(' ')}
      >
        {/* Topo — logo (mesma altura que a Topbar: h-14, sem border-b) */}
        <div className="h-14 flex items-center flex-shrink-0 px-3">
          <div className={`flex items-center w-full ${collapsed ? 'lg:justify-center' : 'justify-start'}`}>
            {collapsed ? (
              <img src="/iasolicitar-icon.svg" alt="IaSolicitar" className="w-8 h-8 flex-shrink-0" />
            ) : (
              <img
                src={effective === 'dark' ? '/iasolicitar-logo-dark.svg' : '/iasolicitar-logo.svg'}
                alt="IaSolicitar"
                className="h-12 w-auto object-contain"
              />
            )}
          </div>
        </div>

        {/* Navegação */}
        <nav className="flex-1 overflow-y-auto overflow-x-hidden py-2 min-h-0">
          {visibleNavigation.map((g, gi) => (
            <Group key={gi} label={g.group} collapsed={collapsed}>
              {g.items.map((it, ii) =>
                isAccordion(it) ? (
                  <AccordionItem
                    key={ii}
                    item={it}
                    collapsed={collapsed}
                    onItemClick={closeMobile}
                  />
                ) : (
                  <LeafItem
                    key={ii}
                    item={it}
                    collapsed={collapsed}
                    onClick={closeMobile}
                  />
                )
              )}
            </Group>
          ))}
        </nav>

        {/* Rodapé — simplificado: avatar + nome + chevron (expandido) / só chevron (colapsado) */}
        <div className="flex-shrink-0 border-t border-line dark:border-line-dark p-2">
          {!collapsed && user ? (
            <div className="flex items-center gap-2 px-2 py-1.5 rounded-lg">
              <div className="w-8 h-8 bg-brand-500 text-white rounded-full flex items-center justify-center text-[12px] font-semibold flex-shrink-0 ring-2 ring-surface dark:ring-surface-dark">
                {user.name?.charAt(0).toUpperCase() || '?'}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[12.5px] font-semibold text-ink-title dark:text-ink-title-dark truncate">
                  {user.name || 'Usuário'}
                </div>
                <div className="text-[10.5px] text-ink-subtitle dark:text-ink-subtitle-dark truncate">
                  {user.role || ''}
                </div>
              </div>
              <button
                type="button"
                onClick={() => setCollapsed(true)}
                title="Recolher menu"
                className="w-7 h-7 flex items-center justify-center rounded-md text-ink-subtitle dark:text-ink-subtitle-dark hover:bg-surface-alt dark:hover:bg-surface-dark-alt hover:text-ink-text dark:hover:text-ink-text-dark transition-colors"
              >
                <PanelLeftClose className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setCollapsed(false)}
              title="Expandir menu"
              className="hidden lg:flex w-full items-center justify-center py-2 rounded-lg text-ink-subtitle dark:text-ink-subtitle-dark hover:bg-surface-alt dark:hover:bg-surface-dark-alt hover:text-ink-text dark:hover:text-ink-text-dark transition-colors"
            >
              <PanelLeftOpen className="w-4 h-4" />
            </button>
          )}
        </div>
      </aside>
    </>
  );
}

// ── subcomponentes ─────────────────────────────────────────────────────────

interface GroupProps {
  label: string;
  collapsed: boolean;
  children?: React.ReactNode;
}

const Group: React.FC<GroupProps> = ({ label, collapsed, children }) => {
  return (
    <div className="px-2 py-1">
      {!collapsed && (
        <div className="px-2 pt-2 pb-1 text-[10.5px] font-semibold uppercase tracking-wider text-ink-subtitle dark:text-ink-subtitle-dark">
          {label}
        </div>
      )}
      <div className="space-y-0.5">{children}</div>
    </div>
  );
};

interface LeafItemProps {
  item: NavLeaf;
  collapsed: boolean;
  onClick?: () => void;
}

const LeafItem: React.FC<LeafItemProps> = ({ item, collapsed, onClick }) => {
  const location = useLocation();
  const active = location.pathname === item.to;
  const Icon = item.icon;

  return (
    <Link
      to={item.to}
      onClick={onClick}
      title={collapsed ? item.label : undefined}
      className={[
        'relative flex items-center gap-2.5 rounded-lg text-[13px] font-medium transition-colors',
        collapsed ? 'justify-center h-9 mx-auto w-9' : 'h-9 px-2.5',
        active
          ? 'bg-brand-50 dark:bg-[rgba(203,106,30,0.15)] text-brand-700 dark:text-brand-300'
          : 'text-ink-text dark:text-ink-text-dark hover:bg-surface-alt dark:hover:bg-surface-dark-alt',
        active
          ? "before:content-[''] before:absolute before:left-0 before:top-2 before:bottom-2 before:w-[2px] before:bg-brand-500 before:rounded-r"
          : '',
      ].join(' ')}
    >
      <Icon
        size={18}
        className={`flex-shrink-0 ${active ? 'text-brand-500 dark:text-brand-400' : 'text-ink-subtitle dark:text-ink-subtitle-dark'}`}
      />
      {!collapsed && <span className="truncate">{item.label}</span>}
    </Link>
  );
};

interface AccordionItemProps {
  item: NavAccordion;
  collapsed: boolean;
  onItemClick?: () => void;
}

const AccordionItem: React.FC<AccordionItemProps> = ({ item, collapsed, onItemClick }) => {
  const location = useLocation();
  const childActive = item.children.some(c => c.to === location.pathname);
  const [open, setOpen] = useState(childActive);
  const Icon = item.icon;

  useEffect(() => {
    if (childActive) setOpen(true);
  }, [childActive]);

  if (collapsed) {
    // Quando colapsada, renderiza só o ícone apontando para o primeiro filho
    return (
      <Link
        to={item.children[0]?.to || '/'}
        title={item.label}
        onClick={onItemClick}
        className={[
          'relative mx-auto w-9 h-9 flex items-center justify-center rounded-lg transition-colors',
          childActive
            ? "bg-brand-50 dark:bg-[rgba(203,106,30,0.15)] text-brand-700 dark:text-brand-300 before:content-[''] before:absolute before:left-0 before:top-2 before:bottom-2 before:w-[2px] before:bg-brand-500 before:rounded-r"
            : 'text-ink-subtitle dark:text-ink-subtitle-dark hover:bg-surface-alt dark:hover:bg-surface-dark-alt',
        ].join(' ')}
      >
        <Icon size={18} className={childActive ? 'text-brand-500' : undefined} />
      </Link>
    );
  }

  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        className={[
          'relative w-full flex items-center gap-2.5 h-9 px-2.5 rounded-lg text-[13px] font-medium transition-colors',
          childActive
            ? "bg-brand-50 dark:bg-[rgba(203,106,30,0.15)] text-brand-700 dark:text-brand-300 before:content-[''] before:absolute before:left-0 before:top-2 before:bottom-2 before:w-[2px] before:bg-brand-500 before:rounded-r"
            : 'text-ink-text dark:text-ink-text-dark hover:bg-surface-alt dark:hover:bg-surface-dark-alt',
        ].join(' ')}
      >
        <Icon
          size={18}
          className={`flex-shrink-0 ${childActive ? 'text-brand-500' : 'text-ink-subtitle dark:text-ink-subtitle-dark'}`}
        />
        <span className="flex-1 text-left truncate">{item.label}</span>
        <ChevronDown
          className={`w-3.5 h-3.5 flex-shrink-0 text-ink-subtitle dark:text-ink-subtitle-dark transition-transform ${
            open ? 'rotate-180' : ''
          }`}
        />
      </button>
      {open && (
        <div className="mt-0.5 ml-3 pl-3 border-l border-line dark:border-line-dark space-y-0.5">
          {item.children.map(c => {
            const active = location.pathname === c.to;
            return (
              <Link
                key={c.to}
                to={c.to}
                onClick={onItemClick}
                className={[
                  'flex items-center gap-2 h-8 px-2.5 rounded-lg text-[12.5px] transition-colors',
                  active
                    ? 'text-brand-700 dark:text-brand-300 font-medium bg-brand-50 dark:bg-[rgba(203,106,30,0.15)]'
                    : 'text-ink-subtitle dark:text-ink-subtitle-dark hover:text-brand-700 dark:hover:text-brand-300 hover:bg-surface-alt dark:hover:bg-surface-dark-alt',
                ].join(' ')}
              >
                <span
                  className={`w-1 h-1 rounded-full flex-shrink-0 ${
                    active ? 'bg-brand-500' : 'bg-ink-placeholder dark:bg-ink-subtitle-dark'
                  }`}
                />
                <span className="truncate">{c.label}</span>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
};
